import React, { useState } from "react";
import { Alert, Button, Form, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { CatalogRecord, Lookups, ResourceName } from "./CatalogAdmin.types.tsx";
import CatalogForbidden from "./CatalogForbidden.tsx";
import DeleteRecordModal from "./DeleteRecordModal.tsx";
import { cellOf, failureMessages, problemOf } from "./model.tsx";
import { resourceSpec } from "./resources.tsx";
import { useCatalogList } from "./useCatalogList.tsx";

interface ResourceListProps {
  resource: ResourceName;
  lookups: Lookups;
  version: number;
  onChanged: () => void;
  onSignOut: () => void;
  canDelete?: boolean;
  onNew?: () => void;
  onEdit?: (record: CatalogRecord) => void;
  onImport?: () => void;
}

const ResourceList: React.FC<ResourceListProps> = ({
  resource,
  lookups,
  version,
  onChanged,
  onSignOut,
  canDelete = false,
  onNew,
  onEdit,
  onImport,
}) => {
  const { t } = useTranslation();
  const spec = resourceSpec(resource);
  const list = useCatalogList(resource, version);
  const [deleting, setDeleting] = useState<CatalogRecord | null>(null);

  if (problemOf(list.error).status === 403) {
    return <CatalogForbidden onSignOut={onSignOut} />;
  }

  const removed = () => {
    setDeleting(null);
    list.reload();
    onChanged();
  };

  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <Form.Control
          className="w-auto flex-grow-1"
          type="search"
          aria-label={t("catalog.list.search")}
          placeholder={t("catalog.list.search")}
          value={list.search}
          onChange={(event) => list.setSearch(event.target.value)}
        />
        {onNew && (
          <Button variant="success" onClick={onNew}>
            {t("catalog.list.new")}
          </Button>
        )}
        {onImport && (
          <Button variant="outline-secondary" onClick={onImport}>
            {t("catalog.list.import")}
          </Button>
        )}
      </div>

      {list.error !== null && (
        <Alert variant="danger">{failureMessages(list.error, () => false, t).join(" ")}</Alert>
      )}

      <div className="card p-2 mb-3">
        <Table responsive hover size="sm" className="catalog-table mb-0">
          <thead>
            <tr>
              {spec.columns.map((column) => (
                <th key={column}>{t(`catalog.fields.${column}`)}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data?.items.map((record) => (
              <tr key={record.id}>
                {spec.columns.map((column) => (
                  <td key={column}>{cellOf(column, record.fields[column], lookups)}</td>
                ))}
                <td className="text-end text-nowrap">
                  {onEdit && (
                    <Button size="sm" variant="link" onClick={() => onEdit(record)}>
                      {t("catalog.list.edit")}
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="link"
                      className="text-danger"
                      onClick={() => setDeleting(record)}
                    >
                      {t("catalog.list.delete")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {list.loading && <Spinner size="sm" className="m-2" />}
        {!list.loading && list.data?.items.length === 0 && (
          <p className="m-2">{t("catalog.list.empty")}</p>
        )}
      </div>

      <div className="d-flex align-items-center gap-2">
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={list.page <= 1}
          onClick={() => list.setPage(list.page - 1)}
        >
          {t("catalog.list.previous")}
        </Button>
        <span>
          {t("catalog.list.page", {
            page: list.page,
            pages: list.pages,
            total: list.data?.total ?? 0,
          })}
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={list.page >= list.pages}
          onClick={() => list.setPage(list.page + 1)}
        >
          {t("catalog.list.next")}
        </Button>
      </div>

      <DeleteRecordModal
        resource={resource}
        record={deleting}
        onHide={() => setDeleting(null)}
        onDeleted={removed}
      />
    </>
  );
};

export default ResourceList;
